import { Loader2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { type PhoneNumber, useConfigureTwilioWebhook } from "@/hooks/use-phone-numbers";
import { refusalOf } from "@/utils/phone-number-rules";

/** Points a Twilio number at Verbeo inside the customer's Twilio account — the
 * retry for an import whose webhook step failed. */
export default function ConfigureWebhookButton({ phoneNumber }: { phoneNumber: PhoneNumber }) {
  const configureWebhook = useConfigureTwilioWebhook();
  const { toast } = useToast();

  const handleConfigure = async () => {
    try {
      await configureWebhook.mutateAsync(phoneNumber.id);
      toast({
        title: "Twilio voice webhook configured",
        description: `Calls to ${phoneNumber.phone_number} now reach Verbeo.`,
      });
    } catch (failure) {
      toast({
        variant: "destructive",
        title: "Twilio voice webhook not configured",
        description: refusalOf(failure, {}, "Twilio could not be reached.").message,
      });
    }
  };

  const configuring = configureWebhook.isPending;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleConfigure}
      disabled={configuring}
      aria-busy={configuring}
      data-testid="phone-configure-webhook"
    >
      {configuring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
      {/* "Voice" disambiguates this from the inbound call webhook in Edit —
          this one points the number at Verbeo inside Twilio's own console. */}
      {configuring ? "Configuring…" : "Configure Twilio Voice Webhook"}
    </Button>
  );
}
